// frontend/src/components/FeedbackForm.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../styles/FeedbackForm.css";

const FeedbackForm = () => {
  const navigate = useNavigate();

  const [feedback, setFeedback] = useState({
    name: "",
    email: "",
    category: "General Suggestion",
    rating: 5,
    comments: "",
    file: null,
  });

  const [message, setMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  

  const ratingLabels = ["Poor", "Average", "Good", "Very Good", "Excellent"];

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    const fetchUserProfile = async () => {
      try {
        const response = await axios.get("/api/auth/profile", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const user = response.data.user;
        setFeedback((prev) => ({
          ...prev,
          name: `${user.firstName} ${user.lastName}`,
          email: user.email || user.emailOrPhone,
        }));
      } catch (err) {
        console.error("Error fetching profile", err);
        navigate("/login");
      }
    };

    fetchUserProfile();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback({ ...feedback, [name]: value });
  };

  const handleFileChange = (e) => {
    setFeedback({ ...feedback, file: e.target.files[0] });
  };

  const handleRatingChange = (e) => {
    setFeedback({ ...feedback, rating: parseInt(e.target.value) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!feedback.name || !feedback.email || !feedback.comments) {
      setMessage("All fields except file are required!");
      return;
    }

    const formData = new FormData();
    for (const key in feedback) {
      formData.append(key, feedback[key]);
    }

    // Optimistic UI: Show success popup immediately
    setMessage("");
    setShowPopup(true);
    
    // Reset form
    setFeedback((prev) => ({
      ...prev,
      category: "General Suggestion",
      rating: 5,
      comments: "",
      file: null,
    }));

    // Send feedback in background
    try {
      await axios.post("/api/feedback", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Success is already shown in popup, no toast needed
    } catch (error) {
      // Only show toast on error
      toast.error("Error submitting feedback. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <>
      {/* Pro-style Success Popup - rendered outside container to avoid stacking context issues */}
      {showPopup && (
        <>
          <div
            className="popup-backdrop"
            onClick={() => {
              setShowPopup(false);
              navigate('/');
            }}
          />
          <div className="popup popup-success-pro notranslate" translate="no">
            <div className="popup-success-graphic notranslate" translate="no">
              <svg width="70" height="70" viewBox="0 0 70 70" fill="none">
                <circle cx="35" cy="35" r="32" stroke="#00bfae" strokeWidth="5" fill="#e6faf7"/>
                <path className="tick-path" d="M22 38L32 48L50 26" stroke="#00bfae" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>
            <div className="popup-success-title notranslate" translate="no">
              Thank You for Your Feedback!
            </div>
            <div className="popup-success-text notranslate" translate="no">
              Your feedback has been submitted successfully.<br />
              <span className="popup-support-message notranslate" translate="no">
                We appreciate your support in making ULJAAPP better for everyone.<br />
                <span style={{ color: "#1976d2", fontWeight: 500 }}>— The ULJAAPP Team</span>
              </span>
            </div>
            <button
              className="feebacksubmit notranslate"
              style={{ marginTop: 18, minWidth: 120 }}
              onClick={() => {
                setShowPopup(false);
                navigate('/');
              }}
              translate="no"
            >
              Close
            </button>
          </div>
        </>
      )}

      <div className="feedback-container">
        <h2>Feedback Form</h2>
        {message && <p className="message">{message}</p>}

        <form onSubmit={handleSubmit}>
          <label>Feedback Type:</label>
          <select
            name="category"
            value={feedback.category}
            onChange={handleChange}
          >
            <option value="General Suggestion">General Suggestion</option>
            <option value="User interface">User interface</option>
            <option value="Feature Request">Feature Request</option>
            <option value="Results accuracy">Results accuracy</option>
            <option value="Other">Other</option>
          </select>

          <label>Rating:</label>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                className={`star ${feedback.rating >= star ? 'filled' : ''} notranslate`}
                onClick={() => setFeedback({ ...feedback, rating: star })}
                aria-hidden="true"
                translate="no"
              >
                ★
              </span>
            ))}
          </div>
          <div className="rating-scale">
            <div className="rating-label">
              {ratingLabels[feedback.rating - 1]}
            </div>
          </div>

          <label>Comments:</label>
          <textarea
            name="comments"
            value={feedback.comments}
            onChange={handleChange}
            placeholder="Write your feedback here..."
            required
          />

          {/* <label>Attach a File (Optional):</label> */}
          {/* <input type="file" onChange={handleFileChange} /> */}

          <button className="feebacksubmit" type="submit">
            Submit Feedback
          </button>
        </form>

      {/* after submission message removed: detailed popup handles confirmation and navigation */}
      </div>
    </>
  );
};

export default FeedbackForm;
